import { Badge } from "@design-lab/shared/ui";
import { remoteImageStaticUrl } from "common/utils/remote-image";
import { ArrowRight, Check, CircleCheckBig } from "lucide-react";

const BEFORE_IMAGE = remoteImageStaticUrl("2/gen/user-retention-modal-pin-before.png", {
  w: 275,
  h: 275
});
const AFTER_IMAGE = remoteImageStaticUrl("2/gen/user-retention-modal-pin-after.png", {
  w: 275,
  h: 275
});

const SAMPLE_MESSAGES = [
  'Add the text "#1 Fan" in white color onto the blue flag.',
  "Change the color of the dog's t-shirt from blue to red."
];

const ARTIST_EDITS = ["Text Added", "Color Changed"];

const FEATURES = [
  "<24 hour turnaround",
  "Direct print, fast delivery",
  "Background removal",
  "Change color",
  "Add text"
];

export function ArtistEditsInfo() {
  return (
    <div data-testid="artist-edits-info" className="space-y-4">
      <div>
        <h2 className="text-lg font-bold">Artist Edits - How it Works?</h2>
        <p className="text-sm text-gray-600">Human artist updates within 24 hours.</p>
      </div>

      <div className="border-3 relative flex rounded-xl border-gray-200 p-1.5">
        {/* Arrow */}
        <div className="absolute left-1/2 top-1/3 z-10 -translate-x-1/2">
          <div className="bg-foreground flex size-8 items-center justify-center rounded-full shadow-md">
            <ArrowRight className="text-background size-5" strokeWidth={2.5} />
          </div>
        </div>

        {/* Left side - Original */}
        <div className="flex flex-1 flex-col gap-3 p-2">
          <div className="flex flex-col items-center gap-2">
            <p className="text-center text-sm font-bold">Original Upload</p>
            <img
              src={BEFORE_IMAGE}
              alt="Before artist edits"
              className="w-full max-w-[180px] object-cover"
            />
          </div>
          <div className="space-y-2">
            <p className="text-sm font-bold text-gray-800">Sample message to Artist:</p>
            {SAMPLE_MESSAGES.map((message, index) => (
              <div
                key={message}
                data-testid={`sample-message-${index}`}
                className="flex items-start gap-2"
              >
                <div className="flex size-5 shrink-0 items-center justify-center rounded-full bg-blue-600 text-xs font-bold text-white">
                  {index + 1}
                </div>
                <p className="text-sm leading-tight text-gray-600">{message}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Right side - Artist Edited */}
        <div className="flex flex-1 flex-col gap-3 rounded-lg bg-[#E3F1FF] p-2">
          <div className="flex flex-col items-center gap-2">
            <div className="flex items-center gap-2">
              <p className="text-center text-sm font-bold">Artist Edited</p>
              <Badge
                data-testid="artist-edits-free-badge"
                size="sm"
                className="bg-green-700 font-medium text-white"
              >
                Free
              </Badge>
            </div>
            <img
              src={AFTER_IMAGE}
              alt="After artist edits"
              className="w-full max-w-[180px] object-cover"
            />
          </div>
          <div className="space-y-2">
            <p className="text-center text-sm font-bold text-gray-800">Artist Edits:</p>
            <div className="flex justify-center">
              <div className="flex flex-col gap-2">
                {ARTIST_EDITS.map(edit => (
                  <div
                    key={edit}
                    data-testid={`artist-edit-${edit}`}
                    className="flex items-center gap-2"
                  >
                    <CircleCheckBig className="size-6 shrink-0 fill-green-700 stroke-white" />
                    <p className="text-sm font-bold text-gray-800">{edit}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Features section */}
      <div className="space-y-3">
        <p className="text-base font-bold">What else do you get with Artist Edit?</p>
        <div className="grid grid-cols-2 gap-2">
          {FEATURES.map(feature => (
            <div
              className="flex items-center gap-2 text-xs font-normal text-gray-900 lg:text-sm"
              key={feature}
            >
              <Check className="size-6 shrink-0 stroke-green-700" />
              <p>{feature}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
